import React from 'react';

export default function EditAttributeModal({ showEditAttributeModal, closeEditAttributeModal, attribute, triggerRefresh }) {
  const [attributeForm, setAttributeForm] = React.useState(null);
  const [updateSubmitting, setUpdateSubmitting] = React.useState(false);
  const [updateSuccess, setUpdateSuccess] = React.useState(null);

  React.useEffect(() => {
    if (showEditAttributeModal && attribute) {
      setAttributeForm({
        attribute_id: attribute.attribute_id || 0,
        name: attribute.name || '',
        slug: attribute.slug || '',
        enable_archives: !!attribute.has_archives,
        type: attribute.type || 'select',
        order: attribute.order || 'menu_order',
      });
      setUpdateSuccess(null);
    }
  }, [showEditAttributeModal, attribute]);

  const handleAttributeField = (k, v) => setAttributeForm((s) => ({ ...s, [k]: v }));

  const submitUpdateAttribute = async (e) => {
    e && e.preventDefault();
    setUpdateSubmitting(true);
    setUpdateSuccess(null);
    try {
      const form = new FormData();
      form.append('action', 'tpwvs_update_attribute');
      form.append('security', window.tpwvs_admin_settings.update_attribute_nonce || '');
      form.append('attribute_id', attributeForm.attribute_id);
      form.append('name', attributeForm.name);
      form.append('slug', attributeForm.slug);
      form.append('enable_archives', attributeForm.enable_archives ? '1' : '0');
      form.append('type', attributeForm.type);
      form.append('order', attributeForm.order);

      const res = await fetch(window.tpwvs_admin_settings.api_url, { method: 'POST', body: form });
      const json = await res.json();
      if (json && json.success) {
        setUpdateSuccess({ message: json.data && json.data.message ? json.data.message : 'Attribute updated' });
        if (triggerRefresh) triggerRefresh();
      } else {
        setUpdateSuccess({ error: json.data && json.data.message ? json.data.message : 'Update failed' });
      }
    } catch (err) {
      setUpdateSuccess({ error: err.message || 'Network error' });
    } finally {
      setUpdateSubmitting(false);
    }
  };

  if (!showEditAttributeModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black opacity-40" onClick={closeEditAttributeModal} />
      <div className="bg-white rounded-lg shadow-lg z-50 w-full max-w-2xl p-6">
        <h4 className="text-xl font-semibold mb-2">Edit Attribute</h4>
        <p className="text-base text-gray-700 mb-3">Update the attribute settings.</p>

        {updateSuccess && updateSuccess.message ? (
          <div className="mb-4">
            <div className="text-green-700">{updateSuccess.message}</div>
            <div className="mt-3"><button onClick={closeEditAttributeModal} className="px-3 py-1 bg-theme text-white rounded text-sm">Close</button></div>
          </div>
        ) : (
          attributeForm && (
            <form onSubmit={submitUpdateAttribute}>
              <div className="grid grid-cols-1 gap-3">
                <label className="block">
                  <div className="text-base text-heading mb-1 font-semibold">Name</div>
                  <input value={attributeForm.name} onChange={(e) => handleAttributeField('name', e.target.value)} className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-base text-gray-900 focus:outline-none focus:border-theme" required />
                </label>

                <label className="block">
                  <div className="text-base text-heading mb-1 font-semibold">Slug</div>
                  <input maxLength={28} value={attributeForm.slug} onChange={(e) => handleAttributeField('slug', e.target.value)} className="block w-full rounded-md border border-gray2 bg-white px-2 py-1 text-base text-gray-900 focus:outline-none focus:border-theme" />
                  <p className="text-sm text-gray-500">Unique slug (max 28 chars).</p>
                </label>

                <div className="flex items-center gap-3">
                  <input id="edit_enable_archives" type="checkbox" checked={!!attributeForm.enable_archives} onChange={(e) => handleAttributeField('enable_archives', e.target.checked)} className="appearance-none h-4 w-4 border border-gray2 rounded-sm checked:bg-theme checked:border-theme focus:outline-none" />
                  <label htmlFor="edit_enable_archives" className="text-sm font-medium">Enable Archives?</label>
                </div>

                <label className="block">
                  <div className="text-base text-heading mb-1 font-semibold">Type</div>
                  <select value={attributeForm.type} onChange={(e) => handleAttributeField('type', e.target.value)} className="w-full rounded-md border border-gray2 px-2 py-1 text-base focus:outline-none focus:border-theme">
                    <option value="select">Select</option>
                    <option value="color">Color</option>
                    <option value="image">Image</option>
                  </select>
                </label>

                <label className="block">
                  <div className="text-base text-heading mb-1 font-semibold">Default sort order</div>
                  <select value={attributeForm.order} onChange={(e) => handleAttributeField('order', e.target.value)} className="w-full rounded-md border border-gray2 px-2 py-1 text-base focus:outline-none focus:border-theme">
                    <option value="menu_order">Custom Ordering</option>
                    <option value="name">Name</option>
                    <option value="name_num">Name(Numeric)</option>
                    <option value="id">Term ID</option>
                  </select>
                </label>

                {updateSuccess && updateSuccess.error && (
                  <div role="alert" className="mt-2 p-2 bg-red-50 border border-red-200 text-sm text-red-800 rounded-md">{updateSuccess.error}</div>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button type="submit" disabled={updateSubmitting} className="px-3 py-1.5 bg-theme text-white rounded text-sm">{updateSubmitting ? 'Saving…' : 'Save'}</button>
                <button type="button" onClick={closeEditAttributeModal} className="px-2 py-1 border rounded text-sm">Cancel</button>
              </div>
            </form>
          )
        )}
      </div>
    </div>
  );
}
